import axios from "axios";
import { cloudRenderingSessionManagementServiceUrl } from "../config";
import type {
  CloudRenderingRegion,
  RegionFilter,
  SessionRating,
  UserPreferences,
  VmSizeInformation,
} from "../hooks/types";

const sessionManagementApiClient = axios.create({
  baseURL: cloudRenderingSessionManagementServiceUrl,
});

export default sessionManagementApiClient;

export async function getUserPreferences() {
  const response =
    await sessionManagementApiClient.get<UserPreferences>("/Users/Preferences");
  return response.data;
}

export async function setUserPreferences(userPreferences: UserPreferences) {
  const response = await sessionManagementApiClient.patch(
    "/Users/Preferences",
    userPreferences,
  );
  return response.data;
}

export async function rateSession(sessionId: string, rating: SessionRating) {
  const response = await sessionManagementApiClient.patch(
    `/v2/Sessions/${sessionId}/Rate`,
    rating,
  );
  return response.data;
}

export async function getVmSizes() {
  const response = await sessionManagementApiClient.get<{
    vmSizes: VmSizeInformation[];
  }>("/VirtualMachines/Sizes");
  return response.data.vmSizes;
}

export interface RegionsResponse {
  regions: CloudRenderingRegion[];
  userPosition: {
    latitude: number;
    longitude: number;
  };
}

export async function getOrderedCloudRenderingRegions({
  appIds,
  ...filters
}: RegionFilter = {}): Promise<RegionsResponse> {
  const response = await sessionManagementApiClient.post<RegionsResponse>(
    "/v2/Regions",
    {
      appIds: appIds?.map(String),
      ...filters,
    },
  );
  return response.data;
}

type UUID = string;

interface SessionDetailsResponse {
  id: UUID;
  sessionCreatedDateTime: string;
  sessionTerminatedDateTime: string | null;
  experienceStartedDateTime: string | null;
}

export async function getSessionDetails(sessionId: string) {
  const response = await sessionManagementApiClient.get<SessionDetailsResponse>(
    `/v2/Sessions/${sessionId}`,
  );
  return response.data;
}

type PaginatedList<TType> = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  items: TType[];
  pageNumber: number;
  totalPages: number;
  totalResults: number;
};

export interface VmFilter {
  includeDestroyed?: boolean;
  userIdentifier?: string;
}

export interface InstalledApp {
  appBuildId: string | null;
  appId: UUID;
  appType?: string | null;
  readonly id?: string | null;
}

type VirtualMachineState =
  | "Created"
  | "Stopped"
  | "Stopping"
  | "Started"
  | "Starting"
  | "Destroyed"
  | "Errored"
  | "Refreshing";

export interface VirtualMachine {
  id: UUID;
  region: string;
  regionDisplayName: string;
  size: string;
  sizeDisplayName: string;
  activeUserIdentifier?: string;
  privateIp?: string;
  publicIp?: string;
  image: string;
  organizationId?: string;
  pulumiState: string;
  state: VirtualMachineState;
  reservedInstanceId?: string;
  isStarted: boolean;
  deviceId: Device["id"];
  isReady: boolean;
  debugModeEnabled: boolean;
  dcvDeployed: boolean;
  cloudXRSignalingDeployed: boolean;
  spatialCommunicationDeployed: boolean;
  xrGatewayDeployed: boolean;
  appsPreInstallationCompleted: boolean;
  knownError?: string | null;
  dateTimeCreated: string;
  firstTimeReadyDateTime?: string;
  expirationDateTime?: string | null;
  installedApps?: InstalledApp[];
}

export interface Device {
  id: UUID;
  identifier?: string | null;
  name?: string | null;
  displayName?: string | null;
  installedApps: InstalledApp[];
  pendingApps: InstalledApp[];
  appsPreInstallationCompleted: boolean;
  version?: string | null;
  vendorName?: string | null;
  modelName?: string | null;
  xrRuntime?: string | null;
}

type VirtualMachineList = PaginatedList<VirtualMachine>;

export async function getVirtualMachines(
  params: VmFilter,
): Promise<VirtualMachineList> {
  const response = await sessionManagementApiClient.get<VirtualMachineList>(
    "/VirtualMachines",
    { params: params },
  );

  return response.data;
}

export async function getVirtualMachine(
  id: VirtualMachine["id"],
): Promise<VirtualMachine> {
  const response = await sessionManagementApiClient.get<VirtualMachine>(
    `/VirtualMachines/${id}`,
  );

  return response.data;
}

export async function destroyVirtualMachine(
  id: VirtualMachine["id"],
): Promise<VirtualMachine> {
  return await sessionManagementApiClient.post(
    `/VirtualMachines/${id}/Destroy`,
  );
}

export async function updateVirtualMachineExpiration(
  id: VirtualMachine["id"],
  expirationTimeSpan: string,
): Promise<VirtualMachine> {
  return (
    await sessionManagementApiClient.put(`/VirtualMachines/${id}/Expiration`, {
      expirationTimeSpan,
    })
  ).data;
}

export async function installAppsOnDevice(
  id: Device["id"],
  appBuildIds: string[],
): Promise<Device> {
  return (
    await sessionManagementApiClient.post(`/Devices/${id}/InstallApps`, {
      buildIds: appBuildIds,
    })
  ).data;
}

export async function getDevice(id: Device["id"]) {
  return (await sessionManagementApiClient.get(`/Devices/${id}`)).data;
}

export type Policy = "Disallowed" | "OptIn" | "OptOut" | "Enforced";

export type RemoteRenderedSettings = {
  maxSoftSessionRunTime?: string | null;
  maxHardSessionRunTime?: string | null;
  sessionInactivityTimeoutTime?: string | null;
  reuseHoldBackTime?: string | null;
};

export type SessionSettings = {
  maxSoftSessionRunTime?: string | null;
  maxHardSessionRunTime?: string | null;
  sessionInactivityTimeoutTime?: string | null;
};

type OrganizationCloudRenderingPolicy = {
  cloudXREncryption: Policy;
  xrEncryptionPolicy?: Policy;
  xrGatewayPolicy: Policy;
  streamingGatewayPolicy: Policy;
  virtualMachinesRequireFixedIpPolicy?: boolean;
  cloudRenderingSettings?: RemoteRenderedSettings;
  vrStandaloneSettings?: SessionSettings;
  desktopRenderedSettings?: SessionSettings;
};

export async function getOrganizationCloudRenderingPolicies() {
  return (
    await sessionManagementApiClient.get<OrganizationCloudRenderingPolicy>(
      "/Organizations/Settings",
    )
  ).data;
}

export interface CreateVirtualMachine {
  region: string;
  size: string;
  image?: string;
  expirationTimeSpan: string;
  debugModeEnabled: boolean;
  requireDCV?: boolean;
  requireCloudXRSignaling?: boolean;
  requireSpatialCommunication?: boolean;
  requireXRGateway?: boolean;
  preInstalledAppBuildIds?: string[];
}

export async function createVirtualMachine(
  vm: CreateVirtualMachine,
): Promise<VirtualMachine> {
  return (await sessionManagementApiClient.post("/VirtualMachines", vm)).data;
}

export interface Position {
  latitude: number;
  longitude: number;
}

export interface VirtualMachineIntentLocationDetails {
  position: Position;
  regionLatencyMs: Record<string, number> | null;
}

export interface VirtualMachineIntentInfrastructureParameters {
  requireDCV: boolean;
  requireCloudXRSignaling: boolean;
  requireSpatialCommunication: boolean;
  requireXRGateway: boolean;
  requireFixedIp: boolean;
}

export interface CreateVirtualMachineIntent {
  image?: string;
  size?: string;
  preInstalledAppBuildIds?: string[];
  regionsWhitelist?: string[];
  virtualMachineIntentInfrastructureParameters: VirtualMachineIntentInfrastructureParameters;
  expirationTimeSpan: string;
  locationDetails: VirtualMachineIntentLocationDetails;
}

export interface VirtualMachineIntentVmAttempt {
  id: string;
  region: string;
  pulumiState: string;
  state: string;
  knownError: string | null;
  isReady: boolean;
}

export interface VirtualMachineIntent {
  id: string;
  organizationId: string;
  image: string;
  size: string;
  preInstalledApps: InstalledApp[];
  regionsWhitelist?: string[];
  infrastructureParameters: VirtualMachineIntentInfrastructureParameters;
  expirationTimeSpan: string;
  error: string | null;
  virtualMachines: VirtualMachineIntentVmAttempt[];
  locationDetails: VirtualMachineIntentLocationDetails;
}

export async function createVirtualMachineIntent(
  intent: CreateVirtualMachineIntent,
): Promise<VirtualMachineIntent> {
  return (
    await sessionManagementApiClient.post("/VirtualMachineIntents", intent)
  ).data;
}

export async function getVirtualMachineIntent(
  id: string,
): Promise<VirtualMachineIntent> {
  return (
    await sessionManagementApiClient.get(`/VirtualMachineIntents/${id}`)
  ).data;
}

export const longerThanExpectedMessage =
  "This operation is taking longer than expected";
